import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { type Size } from "@/assets/icons/utils";
import { ArchivedSidebarContent } from "@/components/archive/ArchivedSidebar";
import { Button } from "@/components/flexkit/Button";
import { Resizable, ResizableHandle } from "@/components/flexkit/Resizable";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { KeyboardShortcutsModal } from "@/components/keyboard/KeyboardShortcutsModal";
import { Nudges } from "@/components/nudges/Nudges";
import { OrganizationAnnouncementBanner } from "@/components/OrganizationAnnouncementBanner";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { cn } from "@/components/podkit/lib/cn";
import { DesktopTrafficLights } from "@/components/sidebar/DesktopTrafficLights";
import { SidebarButton } from "@/components/SidebarButton";
import { SidebarLogoToggle } from "@/components/SidebarLogoToggle";
import { Tooltip } from "@/components/Tooltip";
import { SidebarControllerContext } from "@/contexts/SidebarControllerContext";
import { Flags } from "@/feature-flags";
import { useDesktop } from "@/hooks/use-desktop";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useFlag, useFlagCommandPaletteEnabled, useFlagOnaAIEnabled, useFlagSuperOnaEnabled } from "@/hooks/use-flag";
import { AutomationsAccessLevel, useFeatureAutomations } from "@/hooks/use-feature-automations";
import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useMobileSidebarWidth } from "@/hooks/use-mobile-sidebar-width";
import { useKeyboardHelpShortcut } from "@/hooks/use-keyboard-help-shortcut";
import { useMembership } from "@/hooks/use-membership";
import { useNewEnvironmentShortcut } from "@/hooks/use-open-env-modal-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { useSidebarToggleShortcut } from "@/hooks/use-sidebar-toggle-shortcut";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { useHasArchivedEnvironments } from "@/queries/environment-queries";
import { CreateProjectModal } from "@/components/projects/CreateProjectModal";
import { useSetPreference } from "@/queries/user-preferences-queries";
import { CreateEnvironmentModal } from "@/routes/environments/create/CreateEnvironmentModal";
import { SidebarEnvironmentList } from "@/routes/environments/SidebarEnvironmentList";
import { ToggleSidebarKeyBinding } from "@/utils/platform";
import { IconArchive } from "central-icons/IconArchive";
import { IconHomeOpen } from "central-icons/IconHomeOpen";
import { IconLayoutGrid2 } from "central-icons/IconLayoutGrid2";
import { IconPieChart2 } from "central-icons/IconPieChart2";
import { IconSettingsGear2 } from "central-icons/IconSettingsGear2";
import { IconChevronLeft } from "central-icons/IconChevronLeft";

import { ChangelogButton } from "@/components/changelog/ChangelogButton";
import { IconWorkflows } from "@/assets/icons/central/IconAutomations";
import { archiveViewAtom, commandPaletteOpenAtom, keyboardHelpModalOpenAtom } from "@/atoms/ui-state";
import { CommandPalette } from "@/components/command-palette/CommandPalette";
import { useCommandPaletteShortcut } from "@/components/command-palette/use-command-palette-shortcut";
import { NewEnvironmentButton } from "@/components/NewEnvironmentButton";

import { UserSettingsModal } from "@/components/settings/UserSettingsModal";
import { LIST_TRANSITION_CLASSES, TRANSITION_CLASSES, TRANSITION_DURATION } from "@/components/SidebarButton.variants";
import type { RouteHandle } from "@/route-handle";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useAtom } from "jotai";
import { useMobileSidebarSwipe } from "@/hooks/use-mobile-sidebar-swipe";
import { CirclePlus, MenuIcon, PanelLeftClose, PanelLeftOpen, XIcon } from "lucide-react";
import {
    Suspense,
    lazy,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
    type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import { Outlet, useLocation, useMatches } from "react-router-dom";

const DesktopTitlebar = lazy(() =>
    import("@/components/desktop/DesktopTitlebar").then((m) => ({ default: m.DesktopTitlebar })),
);

const SIDEBAR_STATE_KEY = "sidebar-state";

type Props = {
    CustomSidebar?: React.ComponentType<{ className?: string; maxWidth?: number; minWidth?: number }>;
    /**
     * Determines whether the sidebar should be collapsible.
     */
    disableCollapse?: boolean;
    disableMobileMenu?: boolean;
    CustomContent?: FC;
};

export const SidebarLayout: FC<Props> = ({
    CustomSidebar,
    disableCollapse = false,
    disableMobileMenu = false,
    CustomContent,
}) => {
    const { isDesktop } = useDesktop();

    // Child routes can specify noPadding via their handle property.
    const matches = useMatches();
    const noPadding = matches.some((match) => (match.handle as RouteHandle | undefined)?.layout?.noPadding === true);

    const { value: fullBleed } = useFlagSuperOnaEnabled();

    // Set up dynamic title handling - this will be the fallback for pages that don't set their own title
    useDynamicTitle("Ona");

    const { isMobileViewport } = useIsMobileViewport();
    const mobileSidebarWidthVw = useMobileSidebarWidth();
    const location = useLocation();
    const isNewConversationPage = location.pathname.endsWith("/ai");

    const [isDesktopLocked, setIsDesktopLocked] = useState(() => {
        if (disableCollapse) {
            return true; // if we prevent the sidebar from collapsing, we always show it
        }

        const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
        if (savedState === "unlocked") {
            return false;
        }

        return true;
    });

    const isNoSidebar = CustomSidebar === NoSidebar;
    const showGlobalHeader = CustomSidebar !== NoSidebar && isMobileViewport;

    const [showNewEnvModal, setShowNewEnvModal] = useState(false);
    useNewEnvironmentShortcut(() => {
        setShowNewEnvModal(true);
    });
    const [showKeyboardHelp, setShowKeyboardHelp] = useAtom(keyboardHelpModalOpenAtom);
    useKeyboardHelpShortcut(() => {
        setShowKeyboardHelp(true);
    });
    // Command palette (feature-flagged)
    const { value: commandPaletteEnabled } = useFlagCommandPaletteEnabled();
    const [showCommandPalette, setShowCommandPalette] = useAtom(commandPaletteOpenAtom);
    useCommandPaletteShortcut(commandPaletteEnabled, () => {
        if (commandPaletteEnabled) {
            setShowCommandPalette((prev) => !prev);
        }
    });

    // Create project modal (triggered from command palette)
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

    // Stable callbacks for CommandPalette to avoid re-renders on every SidebarLayout render
    const handleCreateEnvironment = useCallback(() => setShowNewEnvModal(true), []);
    const handleCreateProject = useCallback(() => setShowCreateProjectModal(true), []);
    const handleShowKeyboardShortcuts = useCallback(() => setShowKeyboardHelp(true), [setShowKeyboardHelp]);

    const [isArchiveView, setIsArchiveView] = useAtom(archiveViewAtom);
    const canHoverRef = useRef(true);

    const [playTransition, setPlayTransition] = useState(false);
    const playTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const applyDesktopLockState = useCallback((locked: boolean, sync: boolean) => {
        const update = () => {
            setIsDesktopLocked(locked);
            setPlayTransition(true);
        };

        if (sync) {
            flushSync(update);
            return;
        }

        update();
    }, []);

    const doPlayTransition = useCallback(() => {
        setPlayTransition(true);
        clearTimeout(playTransitionTimeoutRef.current);
        playTransitionTimeoutRef.current = setTimeout(() => {
            setPlayTransition(false);
        }, TRANSITION_DURATION); // reset the playTransition after transition duration
    }, []);

    const toggleDesktopLock = useCallback(() => {
        const newState = !isDesktopLocked;
        localStorage.setItem(SIDEBAR_STATE_KEY, newState ? "locked" : "unlocked");

        // Use a synchronous update for direct user interactions so the sidebar
        // animation starts from the current frame.
        applyDesktopLockState(newState, true);

        if (!newState) {
            canHoverRef.current = false;
        }

        // Reset transition state after animation completes
        const timeout = setTimeout(() => {
            setPlayTransition(false);
            if (!newState) {
                canHoverRef.current = true;
            }
        }, TRANSITION_DURATION);

        return () => clearTimeout(timeout);
    }, [applyDesktopLockState, isDesktopLocked]);

    const lockSidebarInternal = useCallback(
        (sync: boolean) => {
            if (isDesktopLocked) {
                return;
            }

            applyDesktopLockState(true, sync);

            clearTimeout(playTransitionTimeoutRef.current);
            playTransitionTimeoutRef.current = setTimeout(() => {
                setPlayTransition(false);
            }, TRANSITION_DURATION);
        },
        [applyDesktopLockState, isDesktopLocked],
    );

    const lockSidebar = useCallback(() => {
        lockSidebarInternal(true);
    }, [lockSidebarInternal]);

    const lockSidebarPersistent = useCallback(() => {
        if (!isDesktopLocked) {
            localStorage.setItem(SIDEBAR_STATE_KEY, "locked");

            lockSidebar();
        }
    }, [isDesktopLocked, lockSidebar]);

    const unlockSidebarInternal = useCallback(
        (sync: boolean) => {
            if (!isDesktopLocked) {
                return;
            }

            applyDesktopLockState(false, sync);

            canHoverRef.current = false;
            clearTimeout(playTransitionTimeoutRef.current);
            playTransitionTimeoutRef.current = setTimeout(() => {
                setPlayTransition(false);
                canHoverRef.current = true;
            }, TRANSITION_DURATION);
        },
        [applyDesktopLockState, isDesktopLocked],
    );

    const unlockSidebar = useCallback(() => {
        unlockSidebarInternal(true);
    }, [unlockSidebarInternal]);

    const unlockSidebarPersistent = useCallback(() => {
        if (isDesktopLocked) {
            localStorage.setItem(SIDEBAR_STATE_KEY, "unlocked");
            unlockSidebar();
        }
    }, [isDesktopLocked, unlockSidebar]);

    const resetSidebar = useCallback(() => {
        const savedState = localStorage.getItem(SIDEBAR_STATE_KEY);
        if (savedState === "locked") {
            lockSidebarInternal(false);
        } else {
            unlockSidebarInternal(false);
        }
    }, [lockSidebarInternal, unlockSidebarInternal]);

    const sidebarController = useMemo(
        () => ({
            lockSidebar,
            unlockSidebar,
            resetSidebar,
            lockSidebarPersistent,
            unlockSidebarPersistent,
        }),
        [lockSidebar, resetSidebar, unlockSidebar, lockSidebarPersistent, unlockSidebarPersistent],
    );

    // This ensures that we trigger a re-render when the principal changes - this is needed to
    // ensure we re-render when the user is updated.
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal]);

    // when app.tsx gives us showCreateModal, we know that the URL hash has a relevant context URL for us
    useEffect(() => {
        if (location.state?.showCreateModal) {
            setShowNewEnvModal(true);
        }
    }, [location]);

    // When archive view is activated (e.g. from command palette), ensure
    // the sidebar is open so the archive content is visible.
    // Only fires on the false→true transition to avoid fighting a manual unlock.
    const prevArchiveView = useRef(isArchiveView);
    useEffect(() => {
        const wasArchive = prevArchiveView.current;
        prevArchiveView.current = isArchiveView;
        if (isArchiveView && !wasArchive && !isDesktopLocked) {
            lockSidebarInternal(false);
        }
    }, [isArchiveView, isDesktopLocked, lockSidebarInternal]);

    useSidebarToggleShortcut(() => {
        if (disableCollapse) {
            return;
        }

        // Exit archive view if active to prevent broken UI state
        if (isArchiveView) {
            setIsArchiveView(false);
        }

        if (isMobileViewport) {
            setMobileMenuOpen(!mobileMenuOpen);
        } else {
            toggleDesktopLock();
        }
    });

    // Cleanup timeouts when component unmounts
    useEffect(() => {
        return () => {
            if (playTransitionTimeoutRef.current != null) {
                clearTimeout(playTransitionTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        doPlayTransition();

        // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when the onboardingForceHideSidebar changes
    }, []);

    // pages like settings disable collapsing, so we run this effect to reset the sidebar state when we are no longer forced to have a locked sidebar
    useEffect(() => {
        if (!disableCollapse) {
            sidebarController.resetSidebar();
        }
    }, [disableCollapse, sidebarController]);

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
    const mobileSidebarRef = useRef<HTMLDivElement>(null);
    const mobileBackdropRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (mobileMenuOpen) {
            setMobileSidebarVisible(true);
        } else if (mobileSidebarVisible) {
            // Fallback: if onTransitionEnd doesn't fire (e.g. transition interrupted),
            // hide the sidebar after the animation duration + buffer
            const timeout = setTimeout(() => setMobileSidebarVisible(false), 350);
            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to mobileMenuOpen changes
    }, [mobileMenuOpen]);

    const handleMobileOpen = useCallback(() => {
        setMobileSidebarVisible(true);
        setMobileMenuOpen(true);
    }, []);
    const handleMobileClose = useCallback(() => setMobileMenuOpen(false), []);

    useMobileSidebarSwipe({
        isOpen: mobileMenuOpen,
        onOpen: handleMobileOpen,
        onClose: handleMobileClose,
        sidebarRef: mobileSidebarRef,
        backdropRef: mobileBackdropRef,
        sidebarWidthVw: mobileSidebarWidthVw,
        enabled: isMobileViewport && !disableMobileMenu && !isNoSidebar,
    });

    const showSidebar = isMobileViewport ? mobileMenuOpen : true;
    const isLocked = isMobileViewport ? mobileMenuOpen : isDesktopLocked;
    const sidebarClassName = cn(!isMobileViewport && playTransition ? TRANSITION_CLASSES : "");
    const maxWidth = !isMobileViewport && !isDesktopLocked ? 48 : undefined;
    const minWidth = !isMobileViewport && !isDesktopLocked ? 48 : undefined;

    // On navigation, close sidebar instantly (no slide animation)
    useEffect(() => {
        if (mobileMenuOpen && isMobileViewport) {
            setMobileMenuOpen(false);
            setMobileSidebarVisible(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on path change
    }, [location.pathname]);

    return (
        <div className="app-background-resetting h-full">
            {/* Skip to main content link for screen readers */}
            <a
                href="#main-content"
                className={cn(
                    "sr-only left-[50%] top-1 z-[9999] rounded bg-surface-01 text-base text-content-primary shadow-lg outline-none ring-1",
                    "focus:not-sr-only focus:!absolute focus:px-3 focus:py-1", // without the `!` focus:not-sr-only and focus:absolute have the same specificity, which would cause our link to be positioned statically
                )}
            >
                Skip to main content
            </a>
            <div className="app-background flex size-full flex-col overflow-hidden">
                <>
                    {/* we only show the titlebar if we're not also showing a header, because in that case, the <Header> renders its own webkit-app-region */}
                    {isDesktop && !showGlobalHeader && (
                        <Suspense fallback={<div className="h-10 [-webkit-app-region:drag]" />}>
                            <DesktopTitlebar
                                isSidebarLocked={isDesktopLocked}
                                onToggleSidebar={toggleDesktopLock}
                                disableSidebarToggle={disableCollapse}
                            />
                        </Suspense>
                    )}
                    {/* Mobile header for new design */}
                    {isMobileViewport && showGlobalHeader && (
                        <header className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-3">
                                {!disableMobileMenu && (
                                    <Button
                                        variant="link"
                                        size="md"
                                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                        className="text-content-primary"
                                        LeadingIcon={mobileMenuOpen ? IconX : IconMenu}
                                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                                        data-tracking-id="toggle-mobile-menu"
                                    />
                                )}
                                <OnaWordmark className="h-4 w-14" />
                            </div>
                            {isNewConversationPage && (
                                <Button
                                    variant="link"
                                    size="md"
                                    className="text-content-primary"
                                    onClick={() => setShowNewEnvModal(true)}
                                    aria-label="New environment"
                                    data-tracking-id="new-environment"
                                >
                                    <CirclePlus size={20} />
                                </Button>
                            )}
                        </header>
                    )}
                    {/* persistent global header */}
                    {!isMobileViewport && showGlobalHeader && (
                        <Header
                            toggleLock={toggleDesktopLock}
                            isLocked={isLocked}
                            showSidebarToggle={!disableCollapse}
                            disableSidebarToggle={disableMobileMenu}
                        />
                    )}
                </>
                <OrganizationAnnouncementBanner />
                <div className="flex w-full grow flex-row overflow-hidden">
                    {/* Mobile backdrop — always mounted, fades in/out */}
                    {isMobileViewport && !isNoSidebar && (
                        <>
                            {/* Visual dim — covers full screen, no pointer events */}
                            <div
                                ref={mobileBackdropRef}
                                className="pointer-events-none fixed inset-0 z-40 bg-surface-primary/50 transition-[opacity,backdrop-filter] duration-300 ease-out"
                                style={{
                                    opacity: mobileMenuOpen ? 1 : 0,
                                    backdropFilter: mobileMenuOpen ? "blur(2px)" : "blur(0px)",
                                }}
                                aria-hidden="true"
                            />
                            {/* Click target — only the strip right of the sidebar */}
                            <div
                                className={cn(
                                    "fixed inset-y-0 right-0 z-40 transition-opacity duration-300 ease-out",
                                    mobileSidebarVisible ? "pointer-events-auto" : "pointer-events-none",
                                )}
                                style={{
                                    left: `${mobileSidebarWidthVw}vw`,
                                    opacity: mobileMenuOpen ? 1 : 0,
                                }}
                                onClick={() => setMobileMenuOpen(false)}
                                data-tracking-id="close-mobile-menu"
                            />
                        </>
                    )}
                    {/* Mobile sidebar — always mounted, slides via translate3d */}
                    {isMobileViewport && !isNoSidebar && (
                        <div
                            ref={mobileSidebarRef}
                            role="navigation"
                            aria-label="Main navigation"
                            aria-hidden={!mobileSidebarVisible}
                            className={cn(
                                "app-background fixed inset-y-0 left-0 z-50 border-r border-border-base transition-transform duration-300 ease-out",
                                !mobileSidebarVisible && "invisible",
                            )}
                            style={{
                                width: `${mobileSidebarWidthVw}vw`,
                                transform: mobileMenuOpen ? "translate3d(0, 0, 0)" : "translate3d(-100%, 0, 0)",
                            }}
                            onTransitionEnd={(e) => {
                                if (e.target === e.currentTarget && !mobileMenuOpen) {
                                    setMobileSidebarVisible(false);
                                }
                            }}
                            data-track-location={TrackLocations.Sidebar}
                        >
                            {CustomSidebar ? (
                                <CustomSidebar className={sidebarClassName} />
                            ) : (
                                <Sidebar
                                    isArchiveView={isArchiveView}
                                    onArchiveViewChange={setIsArchiveView}
                                    toggleLock={toggleDesktopLock}
                                    isLocked={true}
                                    handleOpenNewEnvModal={() => setShowNewEnvModal(true)}
                                    className={sidebarClassName}
                                    setMobileMenuOpen={setMobileMenuOpen}
                                />
                            )}
                        </div>
                    )}
                    {/* Desktop sidebar */}
                    {!isMobileViewport && (
                        <div
                            className="relative flex-shrink-0 overflow-hidden"
                            onMouseEnter={() => {
                                if (!isDesktopLocked && canHoverRef.current) {
                                    doPlayTransition();
                                }
                            }}
                            onMouseLeave={() => {
                                setPlayTransition(false);
                            }}
                        >
                            <div
                                className={cn("h-full overflow-hidden pt-2", {
                                    invisible: !showSidebar && !playTransition,
                                })}
                                data-track-location={TrackLocations.Sidebar}
                            >
                                {CustomSidebar ? (
                                    <CustomSidebar
                                        className={sidebarClassName}
                                        maxWidth={maxWidth}
                                        minWidth={minWidth}
                                    />
                                ) : (
                                    <Sidebar
                                        isArchiveView={isArchiveView}
                                        onArchiveViewChange={setIsArchiveView}
                                        toggleLock={toggleDesktopLock}
                                        isLocked={isLocked}
                                        handleOpenNewEnvModal={() => setShowNewEnvModal(true)}
                                        className={sidebarClassName}
                                        maxWidth={maxWidth}
                                        minWidth={minWidth}
                                        mobileMenuOpen={mobileMenuOpen}
                                        setMobileMenuOpen={setMobileMenuOpen}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <div
                        className={cn(
                            "size-full flex-grow overflow-hidden",
                            { "p-2": !fullBleed },
                            { "pl-0": !fullBleed && !isNoSidebar && !isMobileViewport },
                            {
                                "pt-0":
                                    !fullBleed && ((isMobileViewport && !disableMobileMenu) || isDesktop),
                            },
                        )}
                    >
                        <SidebarControllerContext.Provider value={sidebarController}>
                            {CustomContent ? (
                                <CustomContent />
                            ) : (
                                <MainContent noPadding={noPadding}>
                                    <Outlet />
                                </MainContent>
                            )}
                        </SidebarControllerContext.Provider>
                    </div>
                    <div
                        className={cn("fixed bottom-14 left-10 z-20", {
                            invisible: !showSidebar && !playTransition,
                        })}
                    >
                        <Nudges />
                    </div>
                </div>
            </div>

            <CreateEnvironmentModal
                open={showNewEnvModal}
                onClose={() => setShowNewEnvModal(false)}
                data-track-location={TrackLocations.SidebarCreateEnvironmentModal}
                initialAutoStart={!!location.state?.autoStart}
            />
            {showKeyboardHelp && <KeyboardShortcutsModal open={showKeyboardHelp} onOpenChange={setShowKeyboardHelp} />}
            <UserSettingsModal />
            {showCreateProjectModal && <CreateProjectModal onClose={() => setShowCreateProjectModal(false)} />}
            {commandPaletteEnabled && showCommandPalette && (
                <CommandPalette
                    open={showCommandPalette}
                    onOpenChange={setShowCommandPalette}
                    onCreateEnvironment={handleCreateEnvironment}
                    onCreateProject={handleCreateProject}
                    onShowKeyboardShortcuts={handleShowKeyboardShortcuts}
                />
            )}
        </div>
    );
};

export const NoSidebar: FC = () => {
    return <div />;
};

export const ResizableSidebar: FC<{
    children: ReactNode;
    className?: string;
    maxWidth?: number;
    minWidth?: number;
    handleClassName?: string;
    onHandleClick?: () => void;
    showHandle?: boolean;
}> = ({ children, className, maxWidth, minWidth, handleClassName, onHandleClick, showHandle = true }) => {
    const { isMobileViewport } = useIsMobileViewport();
    const mobileSidebarWidthVw = useMobileSidebarWidth();

    if (isMobileViewport) {
        return (
            <div className={cn("h-full", className)} style={{ width: `${mobileSidebarWidthVw}vw` }}>
                {children}
            </div>
        );
    }

    return (
        <Resizable
            initialWidth={300}
            maxWidth={maxWidth ?? 500}
            minWidth={minWidth ?? 296}
            autoSaveId="sidebar-width"
            className={cn("h-full", className)}
            fullWidthThreshold={768}
        >
            {children}
            {/* eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- tracking ID is on the inner <button> */}
            {showHandle && <ResizableHandle className={handleClassName} onClick={onHandleClick} />}
        </Resizable>
    );
};

export const Sidebar: FC<{
    isArchiveView: boolean;
    onArchiveViewChange: (isArchive: boolean) => void;
    toggleLock: () => void;
    isLocked: boolean;
    handleOpenNewEnvModal: () => void;
    className?: string;
    maxWidth?: number;
    minWidth?: number;
    mobileMenuOpen?: boolean;
    setMobileMenuOpen: (open: boolean) => void;
}> = ({
    isArchiveView,
    onArchiveViewChange,
    toggleLock,
    isLocked,
    handleOpenNewEnvModal,
    className,
    maxWidth,
    minWidth,
    setMobileMenuOpen,
}) => {
    const { isMobileViewport } = useIsMobileViewport();

    const handleHandleClick = useCallback(() => {
        if (!isLocked) {
            toggleLock();
        }
    }, [isLocked, toggleLock]);

    return (
        <ResizableSidebar
            maxWidth={maxWidth}
            minWidth={minWidth}
            className={className}
            handleClassName={cn("top-4 mr-[1px] h-[calc(100%-32px)]", {
                "!cursor-e-resize": !isLocked,
            })}
            onHandleClick={handleHandleClick}
        >
            <div data-testid="sidebar" className="flex size-full flex-col pb-[6px] pt-0.5">
                {isArchiveView ? (
                    <ArchivedSidebarContent onArchiveViewChange={onArchiveViewChange} />
                ) : (
                    <>
                        {/* ONA Logo Section */}
                        {!isMobileViewport && <SidebarLogoToggle isLocked={isLocked} onToggle={toggleLock} />}
                        {isMobileViewport && (
                            <div className="flex items-center justify-between px-4 py-3">
                                <OnaWordmark className="h-5 w-auto" />
                                <Button
                                    variant="ghost"
                                    className="h-8 w-8 rounded-[4px] border-0 p-0"
                                    size="sm"
                                    onClick={() => setMobileMenuOpen(false)}
                                    aria-label="Close menu"
                                    data-tracking-id="close-sidebar-menu"
                                >
                                    <IconChevronLeft size={20} />
                                </Button>
                            </div>
                        )}
                        <ScrollArea
                            orientation="vertical"
                            className={cn("mr-0.5 flex-grow pr-0 pt-2", {
                                "mr-1.5 pr-0.5 ![scrollbar-gutter:auto]": !isLocked,
                                "pr-[1px]": isLocked && !isMobileViewport, // desktop: space for focus rings
                                "mr-0 pr-2 pt-0": isMobileViewport, // mobile: tighter top spacing
                            })}
                        >
                            <SidebarContent handleOpenNewEnvModal={handleOpenNewEnvModal} isLocked={isLocked} />
                        </ScrollArea>
                        <div className={cn("p-2 pb-2 pt-4", isMobileViewport && "px-3 pb-3 pt-0")}>
                            <SidebarFooter
                                onArchiveViewChange={onArchiveViewChange}
                                isLocked={isLocked}
                                toggleLock={toggleLock}
                            />
                        </div>
                    </>
                )}
            </div>
        </ResizableSidebar>
    );
};

const IconPanelLeftOpen: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <PanelLeftOpen size={sizeMap[size]} />;
};

const IconPanelLeftClose: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <PanelLeftClose size={sizeMap[size]} />;
};

const IconX: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <XIcon size={sizeMap[size]} />;
};

const IconMenu: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <MenuIcon size={sizeMap[size]} />;
};

type HeaderProps = {
    toggleLock: () => void;
    isLocked: boolean;
    showSidebarToggle: boolean;
    disableSidebarToggle?: boolean;
};
const Header: FC<HeaderProps> = ({ toggleLock, isLocked, showSidebarToggle, disableSidebarToggle = false }) => {
    return (
        <div
            className={cn(
                "flex h-9 flex-shrink-0 flex-row items-center justify-between gap-2 border-transparent bg-transparent px-5",
            )}
        >
            <div className="flex gap-2">
                <DesktopTrafficLights />
                {showSidebarToggle && (
                    <Tooltip
                        content={
                            <div className="flex flex-row items-center gap-2">
                                <span>{isLocked ? "Close sidebar" : "Lock sidebar open"}</span>
                                <KeyboardCombo keys={ToggleSidebarKeyBinding} />
                            </div>
                        }
                    >
                        <Button
                            variant="link"
                            size="sm"
                            onClick={disableSidebarToggle ? undefined : toggleLock}
                            LeadingIcon={isLocked ? IconPanelLeftClose : IconPanelLeftOpen}
                            className="text-content-primary focus-visible:outline-content-primary"
                            aria-label={isLocked ? "Close sidebar" : "Lock sidebar open"}
                            disabled={disableSidebarToggle}
                            data-tracking-id="toggle-sidebar-lock"
                        />
                    </Tooltip>
                )}
                <div className="flex min-w-16">
                    <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} />
                </div>
            </div>
            <div className="h-full flex-grow [-webkit-app-region:drag]" />
        </div>
    );
};

export const SidebarFooter: FC<{
    onArchiveViewChange?: (isArchive: boolean) => void;
    isLocked: boolean;
    toggleLock: () => void;
}> = ({ onArchiveViewChange, isLocked, toggleLock }) => {
    const { hasArchivedEnvironments } = useHasArchivedEnvironments();
    const location = useLocation();
    const isSettingsPage = location.pathname.startsWith("/settings");
    const { isMobileViewport } = useIsMobileViewport();

    // Helper to render Settings button
    const settingsContent = useMemo(() => {
        if (isSettingsPage) {
            return null;
        }

        return (
            <SidebarButton
                icon={<IconSettingsGear2 size={20} />}
                label="Organization settings"
                to="/settings"
                hideLabel={!isLocked}
            />
        );
    }, [isSettingsPage, isLocked]);

    const footerBaseClasses = cn("flex flex-col gap-1 w-full");

    // Mobile footer: Org selector on left, Archive + Settings icons on right
    if (isMobileViewport) {
        return (
            <div className={cn(footerBaseClasses, "border-t border-border-subtle pt-4")}>
                <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1 rounded-lg border border-border-subtle">
                        <OrgSwitcher
                            data-track-location={TrackLocations.SidebarOrgSwitcher}
                            isLocked
                            hideKeyboardShortcuts
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        {onArchiveViewChange && (
                            <SidebarButton
                                icon={<IconArchive size={20} />}
                                label="Archive"
                                onClick={() => {
                                    onArchiveViewChange(true);
                                }}
                                showBadge={hasArchivedEnvironments}
                                hideLabel
                                data-tracking-id="mobile-show-archive-view"
                            />
                        )}
                        <ChangelogButton isLocked={false} />
                        {!isSettingsPage && (
                            <SidebarButton
                                icon={<IconSettingsGear2 size={20} />}
                                label="Organization settings"
                                to="/settings"
                                hideLabel
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (onArchiveViewChange) {
        return (
            <div className={cn(footerBaseClasses, "gap-2")}>
                <div className="flex flex-col gap-1">
                    <SidebarButton
                        icon={<IconArchive size={20} />}
                        label="Archive"
                        onClick={() => {
                            if (!isLocked) {
                                toggleLock();
                            }
                            onArchiveViewChange(true);
                        }}
                        showBadge={hasArchivedEnvironments}
                        hideLabel={!isLocked}
                        data-tracking-id="show-archive-view"
                    />
                    {!isLocked && <ChangelogButton isLocked={isLocked} />}
                    {settingsContent}
                </div>
                {!isSettingsPage && (
                    <div className="flex w-full items-center gap-1">
                        <div className="min-w-0 flex-1">
                            <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} isLocked={isLocked} />
                        </div>
                        {isLocked && <ChangelogButton isLocked={isLocked} />}
                    </div>
                )}
            </div>
        );
    }

    // Default state: Show settings and org switcher
    return (
        <div className={cn(footerBaseClasses, "gap-2")}>
            {!isLocked && <ChangelogButton isLocked={isLocked} />}
            {settingsContent}
            {!isSettingsPage && (
                <div className="flex w-full items-center gap-1">
                    <div className="min-w-0 flex-1">
                        <OrgSwitcher data-track-location={TrackLocations.SidebarOrgSwitcher} isLocked={isLocked} />
                    </div>
                    {isLocked && <ChangelogButton isLocked={isLocked} />}
                </div>
            )}
        </div>
    );
};

const SidebarContent: FC<{ handleOpenNewEnvModal: () => void; isLocked: boolean }> = ({
    handleOpenNewEnvModal,
    isLocked,
}) => {
    const location = useLocation();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const { value: isInsightsDisabled } = useFlag(Flags.InsightsDisabled, false);
    const { accessLevel: workflowAccessLevel } = useFeatureAutomations();
    const { active: activeNudge } = useNudges();
    const showAutomationsBadge = activeNudge?.key === NudgeKeys.AUTOMATIONS_LAUNCH;
    const { mutate: setPreference } = useSetPreference();
    const { isMobileViewport } = useIsMobileViewport();

    // Dismiss the automations launch nudge (and badge) when the user navigates to /automations
    useEffect(() => {
        if (showAutomationsBadge && location.pathname.startsWith("/automations")) {
            setPreference({
                key: NudgeKeys.AUTOMATIONS_LAUNCH,
                value: new Date().toISOString(),
            });
        }
    }, [showAutomationsBadge, location.pathname, setPreference]);
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    return (
        <>
            <div className={cn("flex flex-col gap-1 pl-2", isMobileViewport && "pl-3")}>
                <NewEnvironmentButton
                    isLocked={isLocked}
                    onClick={handleOpenNewEnvModal}
                    data-tracking-id="open-new-env-modal"
                />
                {isOnaEnabled && (
                    <SidebarButton
                        icon={<IconHomeOpen size={20} />}
                        label={"Home"}
                        active={location.pathname === "/ai"}
                        to="/ai"
                        hideLabel={!isLocked}
                    />
                )}
                <SidebarButton
                    icon={<IconLayoutGrid2 size={20} />}
                    label="Projects"
                    active={location.pathname.startsWith("/projects")}
                    to="/projects"
                    hideLabel={!isLocked}
                />
                {workflowAccessLevel !== AutomationsAccessLevel.None && (
                    <SidebarButton
                        icon={<IconWorkflows />}
                        label="Automations"
                        active={location.pathname.startsWith("/automations")}
                        to="/automations"
                        hideLabel={!isLocked}
                        showBadge={showAutomationsBadge}
                    />
                )}
                {!isInsightsDisabled && isOrgAdmin && (
                    <SidebarButton
                        icon={<IconPieChart2 size={20} />}
                        label="Insights"
                        active={location.pathname === "/insights"}
                        to="/insights"
                        hideLabel={!isLocked}
                    />
                )}
                <div className="h-2" />
                <Lists isLocked={isLocked} />
            </div>
        </>
    );
};

const Lists = ({ isLocked }: { isLocked: boolean }) => {
    return (
        <div className={cn(LIST_TRANSITION_CLASSES, { "hidden h-0 overflow-hidden": !isLocked })}>
            <SidebarEnvironmentList />
        </div>
    );
};

type MainContentProps = PropsWithChildren<{ noPadding?: boolean }>;

export const MainContent: FC<MainContentProps> = ({ children, noPadding }) => {
    const { value: fullBleed } = useFlagSuperOnaEnabled();
    const { isDesktop } = useDesktop();
    const Comp = noPadding ? "div" : ScrollArea;

    return (
        <main
            id="main-content"
            className={cn("size-full overflow-hidden bg-surface-01", {
                "rounded-lg border border-border-base p-0.5": !fullBleed,
                "border-l border-border-base": fullBleed,
                "rounded-tl-xl border-t border-border-base": fullBleed && isDesktop,
                "p-0": noPadding || fullBleed,
            })}
        >
            <Comp
                orientation="both"
                className={cn("size-full max-w-full", {
                    "p-0": noPadding,
                    "p-6": !noPadding,
                })}
            >
                {children}
            </Comp>
        </main>
    );
};
